import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Download, FileText } from 'lucide-react';
import { API } from '@/App';

// The stored file is prefixed with a UUID, so name the download after the blog itself
const downloadName = (blog) => {
  const source = blog.content_data?.document_url || blog.content_data?.filename || '';
  const ext = source.includes('.') ? source.split('.').pop().split('?')[0] : 'pdf';
  const safeTitle = (blog.title || 'document').replace(/[\\/:*?"<>|]/g, '').trim();
  return `${safeTitle}.${ext}`;
};

const PublicBlogPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setBlog(response.data);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
          <p className="text-gray-600">Blog post not found</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} - 11/11 Collective Connection</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <article className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-12">
          {/* Header */}
          <header className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {blog.title}
            </h1>
            <div className="flex items-center text-sm text-gray-500">
              <time dateTime={blog.created_at}>
                {new Date(blog.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          </header>

          {/* AI Selected Image */}
          {blog.ai_image && (
            <div className="mb-8">
              <img 
                src={blog.ai_image.startsWith('http') ? blog.ai_image : `data:image/png;base64,${blog.ai_image}`} 
                alt={blog.title}
                className="w-full h-64 object-cover rounded-lg"
              />
              {blog.content_data?.image_attribution && (
                <p className="text-xs text-gray-500 mt-2 text-right">
                  Photo by{' '}
                  <a 
                    href={blog.content_data.image_attribution.photographer_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-700"
                  >
                    {blog.content_data.image_attribution.photographer}
                  </a>
                  {' '}on{' '}
                  <a 
                    href={blog.content_data.image_attribution.unsplash_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-700"
                  >
                    Unsplash
                  </a>
                </p>
              )}
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* If blog has uploaded document */}
            {(blog.content_data?.pdf_url || blog.content_data?.document_url) ? (
              <div>
                {blog.content_data.pdf_url ? (
                  /* Display PDF if available */
                  <div className="mb-6 border rounded-lg overflow-hidden">
                    <iframe
                      src={`${process.env.REACT_APP_BACKEND_URL}${blog.content_data.pdf_url}`}
                      className="w-full h-[600px]"
                      title={blog.title}
                    />
                  </div>
                ) : (
                  /* Show download option if only original document available */
                  <div className="mb-6 p-8 bg-blue-50 border-2 border-blue-200 rounded-lg text-center">
                    <div className="text-4xl mb-3">📄</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Document Available</h3>
                    <p className="text-gray-600 mb-4">
                      This blog content is available as a downloadable document.
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap items-start gap-3 mb-6">
                  {blog.content_data.pdf_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${blog.content_data.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      data-testid="blog-open-pdf-link"
                    >
                      <FileText className="w-4 h-4" />
                      Open PDF
                    </a>
                  )}
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${blog.content_data.document_url}`}
                    download={downloadName(blog)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    data-testid="blog-download-link"
                  >
                    <Download className="w-4 h-4" />
                    Download {blog.title}
                  </a>
                </div>
                {blog.content_data.summary && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Summary:</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{blog.content_data.summary}</p>
                  </div>
                )}
              </div>
            ) : blog.content_data?.html_content || blog.content_data?.text ? (
              /* Text-based blog content */
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: blog.content_data.html_content || blog.content_data.text
                }} 
              />
            ) : (
              /* No content available */
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">Content coming soon...</p>
                <p className="text-sm">This blog post is still being prepared.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="mt-12 pt-6 border-t border-gray-200" data-testid="blog-footer">
            <p className="text-sm text-gray-500 text-center" data-testid="blog-copyright-notice">
              © {new Date().getFullYear()} 11/11 Collective Connection. Human-directed framework,
              curated content, and proprietary methodology. All rights reserved.
            </p>
            <p className="text-xs text-gray-400 text-center italic mt-2" data-testid="blog-ai-disclosure">
              This material is part of the proprietary 11/11 Collective Connection methodology. AI was
              used as a drafting assistant; all content was human-directed, curated, and finalized.
            </p>
          </footer>
        </article>
      </div>
    </>
  );
};

export default PublicBlogPage;
